/**
 * The utterance buffer — pure functions, no React, no storage.
 *
 * This is the single source of truth for what the person is saying (design §3). It is pure
 * because it sits on the < 50 ms keypress path and because its edge cases are exactly the ones
 * that are miserable to debug on a borrowed iPad: deleting a word that ends in an umlaut,
 * undoing past the start, a caret that drifts by one.
 *
 * German-aware throughout. `Wort löschen` on `Grossvater-Haus` removes the whole compound,
 * because a hyphenated compound is one word to the person who typed it.
 */

export interface ComposerState {
  text: string;
  /** Caret index into `text`. Always 0..text.length. */
  caret: number;
  undoStack: ReadonlyArray<{ text: string; caret: number }>;
}

/**
 * Deep enough to undo a mis-hit run without becoming a memory question. A person correcting
 * tremor errors may undo several times in a row; nobody undoes fifty.
 */
export const UNDO_DEPTH = 50;

export function createComposer(text = ''): ComposerState {
  return { text, caret: text.length, undoStack: [] };
}

/**
 * Characters that belong to a word. `\p{L}` covers umlauts without enumerating them, `\p{N}`
 * keeps digits attached, and the hyphen and apostrophe keep German compounds and elisions
 * whole. Deliberately no `ß` special case — it cannot be typed (Swiss orthography) and the
 * seed data is folded to `ss`.
 *
 * The colon is *not* a word character, so `Wort löschen` after `14:30` removes `30` and leaves
 * `14:`. That is a deliberate trade: treating `:` as interior would help times and hurt every
 * other use of it, and a second delete finishes the job either way.
 */
const WORD_CHAR = /[\p{L}\p{N}\-']/u;

const isWordChar = (char: string | undefined): boolean => char !== undefined && WORD_CHAR.test(char);

/**
 * Split an utterance into words, using exactly the boundaries this composer uses.
 *
 * Exported because `LearningService` has to count the same words the person typed. A second regex
 * living in the learning code would drift — the day someone adjusts hyphen handling here, the n-grams
 * would quietly start keying on different tokens than the predictions look up, and prediction would
 * appear to stop learning for no visible reason.
 */
export function splitWords(text: string): string[] {
  const words: string[] = [];
  let current = '';
  for (const char of text) {
    if (isWordChar(char)) {
      current += char;
    } else if (current !== '') {
      words.push(current);
      current = '';
    }
  }
  if (current !== '') words.push(current);
  return words;
}

function push(state: ComposerState, next: { text: string; caret: number }): ComposerState {
  const undoStack = [...state.undoStack, { text: state.text, caret: state.caret }].slice(
    -UNDO_DEPTH,
  );
  return { ...next, undoStack };
}

export function insert(state: ComposerState, insertion: string): ComposerState {
  if (insertion === '') return state;
  const text = state.text.slice(0, state.caret) + insertion + state.text.slice(state.caret);
  return push(state, { text, caret: state.caret + insertion.length });
}

/**
 * Sentence terminators the keyboard can actually produce.
 *
 * `!` and `?` had no key when this was written and they have had one since the second layer shipped
 * (`GEO-20`) — the regex was already right, so capitalising after a question mark needed no code. Kept as a
 * named constant rather than inlined because `atSentenceStart` and `Wort löschen` must agree about it.
 */
const SENTENCE_END = /[.!?]/u;

/**
 * Whether a letter typed at `index` would begin a sentence.
 *
 * The whitespace requirement is the interesting half: a full stop with nothing after it is as likely to
 * be an abbreviation as the end of a sentence, so `z.B` continues in lowercase while `Hallo. ` starts a
 * new capital. Only the space tells the two apart.
 */
export function atSentenceStart(text: string, index: number): boolean {
  const before = text.slice(0, index);
  const trimmed = before.replace(/\s+$/u, '');
  if (trimmed === '') return true;
  if (trimmed === before) return false;
  return SENTENCE_END.test(trimmed.slice(-1));
}

/**
 * Insert a keystroke, cased as German rather than as the keycap.
 *
 * The keys are drawn in capitals and always will be: one uppercase glyph per key is the most legible
 * thing a person with an unmeasured visual impairment can be given, and the layout note is explicit that
 * there is no shift state because a second layer costs too much for impaired motor control. But a keycap
 * is a label, not the letter — so `A` types `a`, and the sentence's first letter is capitalised by rule.
 *
 * Separate from `insert` deliberately. Abbreviation expansions, accepted predictions and phrase text
 * arrive already spelled correctly and must pass through untouched; only keystrokes are re-cased.
 */
export function insertTyped(state: ComposerState, key: string): ComposerState {
  if ([...key].length !== 1 || !/\p{L}/u.test(key)) return insert(state, key);
  return insert(state, atSentenceStart(state.text, state.caret) ? key.toUpperCase() : key.toLowerCase());
}

/**
 * Re-spell the word before the caret, when only its capitalisation changes.
 *
 * German capitalises its nouns and this keyboard has no shift, so a word the person spells out in full
 * would read as wrong to whoever is listening. `canonicalCasing` in the lexicon supplies the spelling;
 * this applies it, and refuses anything that is not purely a case change.
 *
 * Three refusals, each with a reason:
 *  - **Not case-only.** A rewrite that changes letters is a different word, and choosing a different word
 *    is what accepting a candidate is for. This must never be able to produce something untyped.
 *  - **At a sentence start.** The capital there was put on by rule, and the corpus's answer for `wie` is
 *    `wie` — applying it would quietly undo the sentence's first capital on every sentence.
 *  - **No undo step.** Case is not an edit the person made and should not cost them an `undo` to get
 *    past. The caret cannot move either: the two spellings are the same length.
 */
export function recaseWordBeforeCaret(state: ComposerState, canonical: string): ComposerState {
  const word = wordBeforeCaret(state);
  if (word === '' || word === canonical) return state;
  if (word.toLowerCase() !== canonical.toLowerCase()) return state;

  const start = state.caret - word.length;
  if (atSentenceStart(state.text, start)) return state;

  return {
    ...state,
    text: state.text.slice(0, start) + canonical + state.text.slice(state.caret),
  };
}

/** Delete the character before the caret. A no-op at the start, never an error. */
export function deleteChar(state: ComposerState): ComposerState {
  if (state.caret === 0) return state;
  // Step back over a full grapheme where possible, so a combining mark does not leave a
  // half-formed letter on screen.
  const before = state.text.slice(0, state.caret);
  const removed = [...before].at(-1) ?? '';
  const text = before.slice(0, before.length - removed.length) + state.text.slice(state.caret);
  return push(state, { text, caret: state.caret - removed.length });
}

/**
 * Delete the word before the caret, plus the whitespace that separated it.
 *
 * Trailing whitespace goes first, so `Hallo ` deletes to `` in one action rather than leaving
 * a stray space that makes the next word look joined-up when spoken.
 */
export function deleteWord(state: ComposerState): ComposerState {
  if (state.caret === 0) return state;

  const chars = [...state.text.slice(0, state.caret)];
  let end = chars.length;

  while (end > 0 && /\s/u.test(chars[end - 1]!)) end -= 1;

  if (end > 0 && !isWordChar(chars[end - 1])) {
    // Punctuation directly before the caret: remove just that run, so deleting after
    // "Wasser, bitte." does not swallow "bitte" too.
    while (end > 0 && !isWordChar(chars[end - 1]) && !/\s/u.test(chars[end - 1]!)) end -= 1;
  } else {
    while (end > 0 && isWordChar(chars[end - 1])) end -= 1;
  }

  const keptBefore = chars.slice(0, end).join('');
  return push(state, {
    text: keptBefore + state.text.slice(state.caret),
    caret: keptBefore.length,
  });
}

export function clear(state: ComposerState): ComposerState {
  if (state.text === '') return state;
  return push(state, { text: '', caret: 0 });
}

/**
 * Replace `[start, end)` with `replacement`, as **one** undo step.
 *
 * Used by abbreviation expansion. One step matters: a wrong expansion must cost exactly one action
 * to reverse, not one per character it inserted.
 */
export function replaceRange(
  state: ComposerState,
  start: number,
  end: number,
  replacement: string,
): ComposerState {
  const from = Math.max(0, Math.min(start, state.text.length));
  const to = Math.max(from, Math.min(end, state.text.length));
  const text = state.text.slice(0, from) + replacement + state.text.slice(to);
  return push(state, { text, caret: from + replacement.length });
}

/** Step back one edit. Undoing with nothing to undo is a no-op, not an error. */
export function undo(state: ComposerState): ComposerState {
  const previous = state.undoStack.at(-1);
  if (!previous) return state;
  return { ...previous, undoStack: state.undoStack.slice(0, -1) };
}

export function moveCaret(state: ComposerState, caret: number): ComposerState {
  const clamped = Math.max(0, Math.min(caret, state.text.length));
  return clamped === state.caret ? state : { ...state, caret: clamped };
}

/** The word immediately before the caret — Phase 2's prediction context. */
export function wordBeforeCaret(state: ComposerState): string {
  const chars = [...state.text.slice(0, state.caret)];
  let start = chars.length;
  while (start > 0 && isWordChar(chars[start - 1])) start -= 1;
  return chars.slice(start).join('');
}

/** What would be spoken: trimmed, with no interior double spaces. */
export function utterance(state: ComposerState): string {
  return state.text.replace(/\s+/gu, ' ').trim();
}

export function isEmpty(state: ComposerState): boolean {
  return utterance(state) === '';
}
